import { useMutation, useQueryClient, UseMutationOptions } from '@tanstack/react-query';
import { updateLitter, type Litter } from '@/api/entities/litters';
import { ExtractFnReturnType } from '@/lib/react-query';
import { useCognitoAuth } from '@/hooks/useCognitoAuth';
import { GraphQLRequestError } from '@/api/graphql';

type UpdateLitterParams = {
  id: string;
  input: Partial<Litter>;
};

type QueryFnType = typeof updateLitter;

type UseUpdateLitterOptions = {
  config?: Omit<
    UseMutationOptions<ExtractFnReturnType<QueryFnType>, GraphQLRequestError, UpdateLitterParams>,
    'mutationFn'
  >;
};

export const useUpdateLitter = ({ config }: UseUpdateLitterOptions = {}) => {
  const queryClient = useQueryClient();
  const { getAccessToken } = useCognitoAuth();

  return useMutation<ExtractFnReturnType<QueryFnType>, GraphQLRequestError, UpdateLitterParams>({
    ...config,
    mutationFn: async ({ id, input }: UpdateLitterParams) => {
      return updateLitter(id, input, getAccessToken);
    },
    onSuccess: (data, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({ queryKey: ['litters'] });
      queryClient.invalidateQueries({ queryKey: ['litters', variables.id] });
      config?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
};
